import RoundButton from "features/shared/components/buttons/RoundButton";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import PriceFormatter from "features/shared/components/formatters/PriceFormatter";
import React, { useMemo, useState } from "react";
import { Collapse } from "react-bootstrap";
import PaymentPrices from "features/payments/components/PaymentPrices";
import PaymentUsage from "features/payments/components/PaymentUsage";
import DepositUsageRow from "./DepositUsageRow";
import { getDepositDetailRoute } from "features/shared/helpers/routeHelper";
import { DepositSimplifiedDTO } from "../dtos/DepositSimplifiedDTO";
import PaymentUsageDTO from "features/payments/dtos/PaymentUsageDTO";
import { ElementSize } from "features/shared/enums/elementSize";
import { useTranslation } from "react-i18next";
import { DepositType } from "features/payments/enums/depositType";

interface IProps {
  deposit: DepositSimplifiedDTO;
  showInvoiceTimePeriod?: boolean;
}

const DepositCard = ({ deposit, showInvoiceTimePeriod }: IProps) => {

  const t = useTranslation("deposits\\enums\\depositType").t;

  const payment = deposit.payment;
  const usages: PaymentUsageDTO[] = useMemo(
    () => payment?.paymentUsage ?? [],
    [payment?.paymentUsage]
  );

  const currency = useMemo(
    () => payment?.currency ?? deposit.currency,
    [payment?.currency, deposit.currency]
  );

  const [paymentUsageOpen, setPaymentUsageOpen] = useState<boolean>(false);
  const togglePaymentHandler = () => setPaymentUsageOpen((o) => !o);

  const getPaymentUsageCount = (list?: PaymentUsageDTO[]) => list?.length ?? 0;

  const renderButton = (paymentUsageCount: number, depositId: string) => {
    if (paymentUsageCount > 1) {
      return (
        <RoundButton
          onClickHandler={togglePaymentHandler}
          icon="chevron-down"
          size={ElementSize.SM}
        />
      );
    } else {
      return <RoundButton asLink to={getDepositDetailRoute(depositId)} />;
    }
  };

  const renderDepositType = () => {
    const v = deposit.depositType;
    if (v == null) return <span>-</span>;
    const key = typeof v === "number" ? DepositType[v] : v;
    return <span>{t(key, { defaultValue: key })}</span>;
  };

  const renderUsegeRows = (
    paymentUsage: PaymentUsageDTO[],
    depositId: string
  ): React.ReactElement | undefined => {
    const paymentUsageCount = getPaymentUsageCount(paymentUsage);

    if (paymentUsageCount > 1) {
      return (
        <Collapse in={paymentUsageOpen}>
          <div>
            {paymentUsage?.map((item, index) => (
              <DepositUsageRow
                paymentUsage={item}
                depositId={depositId}
                showInvoiceTimePeriod={showInvoiceTimePeriod}
                key={index}
              />
            ))}
          </div>
        </Collapse>
      );
    }
  };

  const usageCount = getPaymentUsageCount(usages);

  return (
    <>
      <div className="row align-items-center no-gutters">
        <div className="col">
          <div className="row align-items-center no-gutters">
            <div className="col-auto col-sm-2">
              <div className="d-flex flex-sm-column">
                  <DateFormatter date={deposit.paymentDate!} />
                <span className="mx-2 small text-muted d-sm-none">|</span>
              </div>
            </div>

          <div className="d-none d-sm-block col-2">
            <div className="text-truncate">{renderDepositType()}</div>
          </div>

            <div className="col col-sm-3">
              {deposit.amount != null && currency ? (
                <PaymentPrices amount={deposit.amount} currency={currency} paymentUsage={usages}/>
              ) : (
                <div className="d-flex flex-column">
                  <div className="text-truncate">
                    {currency != null ? (
                      <PriceFormatter amount={deposit.amount ?? 0} currency={currency} />
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="col-12 col-sm-5">
              <PaymentUsage paymentUsage={usages} showInvoiceTimePeriod={showInvoiceTimePeriod}/>
            </div>
          </div>
        </div>

        <div className="col-auto">
          {renderButton(usageCount, deposit.depositId!)}
        </div>
      </div>

      {renderUsegeRows(usages, deposit.depositId!)}
    </>
  );
};

export default DepositCard;
